import { Box, Paper, useMediaQuery } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import BubbleChart from "src/components/ScoringChart/BubbleChart";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import TitleTypography, { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringContext,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";
import ButtonAddressTextField from "../ButtonAddressTextField";
import TransactionHistoryTable from "./TransactionHistoryTable";

const getOptions = () => ({
  chart: {
    height: 400,
  },
  xAxis: {
    type: "datetime",
    labels: {
      enabled: true,
    },
  },
  yAxis: [
    {
      labels: {
        enabled: false,
      },
      title: {
        text: "",
      },
      gridLineWidth: 0,
    },
    {
      labels: {
        enabled: false,
      },
      opposite: true,
      title: {
        text: "",
      },
      gridLineWidth: 0,
    },
  ],
  tooltip: {
    useHTML: true,
    style: {
      display: "block",
    },
    formatter: function () {
      let data = "";
      let x = this.x;
      const color = this.color;
      data += `<br /><div class="scoring-chart-tooltip-container">\
        <div class="scoring-chart-tooltip-circle" style="background:${color}"></div>\
        <span>${this.series.name}</span>:<b>&nbsp;$${numberWithCommas(toFixed(this.y, 2))}</b></div>`;
      data = `<div class="scoring-chart-tooltip-x">${Highcharts.dateFormat("%b %e, %Y, %k:%M", x)} UTC</div>` + data;
      return data;
    },
  },
  legend: {
    enabled: true,
  },
});

const dateOption = () => {
  var currentTime = Math.floor(Date.now() / 1000);
  return [
    { label: "1D", from: currentTime - 86400, to: currentTime, id: "1D" },
    { label: "1W", from: currentTime - 604800, to: currentTime, id: "1W" },
    { label: "1M", from: currentTime - 2592000, to: currentTime, id: "1M" },
    { label: "3M", from: currentTime - 7776000, to: currentTime, id: "3M" },
  ];
};

export default function TransactionHistory() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { selectedAddress, setSelectedAddress } = useUserMonitoringContext();
  const { role } = useUserContext();

  const { fetchTransactionHistory } = useUserMonitoringSelector((state) => state.fetch);
  const transactionHistory = useUserMonitoringChartSelector(MONITORING.USER.transactionHistory, pool);

  useEffect(() => {
    if (transactionHistory?.active === undefined) {
      var currentTime = Math.floor(Date.now() / 1000);
      fetchTransactionHistory(pool, { from: currentTime - 604800, to: currentTime }, { active: 1 });
    } else fetchTransactionHistory(pool, transactionHistory?.metadata, { active: transactionHistory?.active });
  }, [transactionHistory?.active, pool, fetchTransactionHistory]);

  const checkData = transactionHistory?.dataTable.length > 0;
  const series = useMemo(() => {
    return [
      {
        name: "Deposit",
        data: [].concat(transactionHistory?.deposit),
      },
      {
        name: "Borrow",
        data: [].concat(transactionHistory?.borrow),
      },
      {
        name: "Withdraw",
        data: [].concat(transactionHistory?.withdraw),
      },
      {
        name: "Repay",
        data: [].concat(transactionHistory?.repay),
      },
    ];
  }, [transactionHistory]);

  return (
    <DetailMonitoringWrapper
      isAuthorization={true}
      navigation={{ text: "user monitoring", name: "transaction history", link: `/user-monitoring/${pool}` }}
      selectLink="/user-monitoring/transaction-history"
      Component={{
        description: (
          <NoteTypography>
            The chart shows the transaction history of a wallet. Each spot implies a transaction which is classified by
            different colors. The height of the spot corresponds with the transaction volume. Based on the graph, it is
            possible to detect the abnormal behavior of the wallet.
          </NoteTypography>
        ),
      }}
    >
      {role === "admin" && (
        <ButtonAddressTextField
          defaultValue={selectedAddress}
          events={{ onConfirm: (value) => setSelectedAddress(value) }}
        />
      )}
      <Paper style={{ marginTop: "1rem" }}>
        <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center">
          <DateGroupButton
            fullWidth={match}
            defaultActive={transactionHistory?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchTransactionHistory(pool, { from, to }, { active });
              },
            }}
            config={dateOption().map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchTransactionHistory(pool, { from: data.from, to: data.to }, { active });
              },
            }))}
          />
        </Box>
        {checkData ? (
          <>
            {role === "admin" && <BubbleChart series={series} option={getOptions()} />}
            <TransactionHistoryTable dataTable={transactionHistory?.dataTable} pool={pool} />
          </>
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </Paper>
    </DetailMonitoringWrapper>
  );
}
